import VueRouter, { Route } from 'vue-router';
import store from '../store';
import { utils } from '../store/utils';
import tokenHelper from '@/utils/tokenHelper';
import { hydrateAuthFromPeers } from '@/utils/hydrate-auth-session';
import client from '@/endpoints';

const handleNext = (
  to: Route,
  next: any,
  router: VueRouter,
  name: string,
  isRedirect: boolean,
) => {
  if (router.currentRoute.name !== name) {
    try {
      if (isRedirect) {
        next({
          name,
          params: { redirect: to.fullPath },
        });
      } else {
        next({
          name,
        });
      }
    } catch (e) {
      const error: Error = e as Error;
      // It's common to ignore the redundant navigation error, but you might want to check for other types of errors as well
      if (error?.name === 'NavigationDuplicated') {
        console.warn(error);
      } else {
        console.log(error);
      }
    }
  }
};

const redirectToLogin = (to: Route, next: any, router: VueRouter) => {
  handleNext(to, next, router, 'Login', true);
  store.commit('updateSnackbar', {
    message: 'Log in to access this page',
    type: 'error',
    hasClose: false,
    hasIcon: false,
    autoClose: true,
  });
};

const handleQueryAuth = (to: Route, next: any, router: VueRouter) => {
  if (to.query.auth) {
    store.dispatch('setToken', to.query.auth);
    store.dispatch('setUserDetails', to.query.auth);
    handleNext(to, next, router, 'Home', false);
    return true;
  }
  return false;
};

const handleProtectedRoutes = (
  to: Route,
  from: Route,
  next: any,
  token: string | null,
  router: VueRouter,
) => {
  if (!token && !tokenHelper.isTokenExpired(token)) {
    redirectToLogin(to, next, router);
    return;
  }

  if (from.params.redirect && from.params.redirect !== to.fullPath) {
    router.push(from.params.redirect);
  } else if (
    to.path === '/open-account' &&
    localStorage.getItem('role') === 'client'
  ) {
    handleNext(to, next, router, 'Home', false);
  } else if (from.path === '/open-account') {
    handleOpenAccountExitModal(to, next);
  } else {
    next();
  }
};

const handleOpenAccountExitModal = (to: Route, next: any) => {
  if (utils.state.openAccountExitModal.isOpen) {
    next();
    store.commit('openAccountExitModal', { isOpen: false, toPath: '' });
  } else {
    store.commit('openAccountExitModal', { isOpen: true, toPath: to.path });
  }
};

const middleware = async (
  to: Route,
  from: Route,
  next: any,
  router: VueRouter,
) => {
  await hydrateAuthFromPeers();

  const token: string | null = client.getBearerToken();

  if (handleQueryAuth(to, next, router)) return;

  if (to?.meta?.requiresAuth) {
    handleProtectedRoutes(to, from, next, token, router);
  } else if (to?.meta?.isGuest && to.meta.validateAuth && token) {
    handleNext(to, next, router, 'Home', false);
  } else {
    next();
  }
};

export default middleware;
